import { useCallback, useEffect, useMemo, useState } from "react"
import { DemandeAbsenceType } from "../../api/DemandeAbsence/types"
import { Dialog } from "../../components"
import { Text, Pressable, TextInput, View } from "react-native"
import styles from './EspaceValideur.styles'
import ValideurDetailRow from "./ValideurDetailRow"
import { AffichagePeriode } from "../../components/AffichagePeriode/AffichagePeriode"
import dayjs from 'dayjs'
import { axiosInstance } from "../../api/Middleware"
import { useSessionStore } from "../../lib/Store/sessionStore.component"
import { DotationEsag } from "../../api/Dotation/types"
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons'
import PastilleStatutDemande from "../../components/StatutDemande/PastilleStatutDemande"

type Props = {
    status: '' | 'info' | 'accept' | 'reject',
    demandeAbsence: DemandeAbsenceType,
    onValidate: (demande: DemandeAbsenceType, commentaire?: string) => void,
    onReject: (demande: DemandeAbsenceType, commentaire?: string) => void,
    onClose: () => void,
}
export default function ValideurDetailDemande({status, demandeAbsence, onValidate, onReject, onClose} : Props){
    const { getCurrentInfos } = useSessionStore();
    const { currentEntite, currentLogin } = getCurrentInfos();
    const [commentaire, setCommentaire] = useState<string>();
    const [droits, setDroits] = useState<number>();
    const handleValidate = useCallback(() => onValidate(demandeAbsence, commentaire), [commentaire, demandeAbsence, onValidate])
    const handleReject = useCallback(() => onReject(demandeAbsence, commentaire), [commentaire, demandeAbsence, onReject])
    const dureeData = useMemo(() => {
        if(droits){
            const formatJour = demandeAbsence.duree?.profil === 1
            return({
                avant: droits,
                apres: droits - (formatJour ? demandeAbsence.duree?.dureeJours || 0 : demandeAbsence.duree?.dureeHeures || 0),
                unite: formatJour ? 'jours' : 'heures',
            })
        }
        return null;
    }, [demandeAbsence.duree?.dureeHeures, demandeAbsence.duree?.dureeJours, demandeAbsence.duree?.profil, droits])
    useEffect(() => {
        const isCP = demandeAbsence.natureAbsence?.code === 'CP'
        const typeAPI = isCP ? 'sconges' : 'squotas'
        axiosInstance.get<DotationEsag>(
            `/personnel/personnel/${typeAPI}/dotationEsag?identifiantAgent=${currentLogin?.identifiant}&identifiantEntite=${currentEntite?.identifiantEntite}&dateAcquisition=${dayjs().startOf('year').format('YYYYMMDD')}`
        ).then((response) => setDroits(Number(response.data.reste.replace(',', '.'))))
    }, [currentEntite?.identifiantEntite, currentLogin?.identifiant, demandeAbsence.natureAbsence?.code])
    return(
        <Dialog visible title={`Demande d'absence de ${demandeAbsence.agent?.nomClassement}`} onClose={onClose}>
            <View style={styles.dialogContainer}>
                {status === 'accept' && (
                    <ValideurDetailRow title="Statut retenu" inline>
                        <PastilleStatutDemande statut="acceptee"/>
                    </ValideurDetailRow>
                )}
                {status === 'reject' && (
                    <ValideurDetailRow title="Statut retenu" inline>
                        <PastilleStatutDemande statut="refusee"/>
                    </ValideurDetailRow>
                )}
                <ValideurDetailRow title="Type d'absence">
                    <Text style={styles.title}>
                        {demandeAbsence.natureAbsence?.libelle}
                    </Text>
                </ValideurDetailRow>
                <ValideurDetailRow title="Période demandée">
                    <AffichagePeriode dateDebut={demandeAbsence.dateDebut} dateFin={demandeAbsence.dateFin} />
                </ValideurDetailRow>
                <ValideurDetailRow title="Date de création">{dayjs(demandeAbsence.dateDemande).format('DD/MM/YYYY')}</ValideurDetailRow>
                {dureeData && (
                    <ValideurDetailRow title="Solde">
                        <View style={styles.soldeContainer}>
                            <View style={styles.soldeElement}>
                                <Text style={styles.sectionTitle}>Avant</Text>
                                <Text>{dureeData.avant} {dureeData.unite}</Text>
                            </View>
                            <View style={styles.soldeElement}>
                                <View style={styles.pastilleFleche}>
                                    <MaterialCommunityIcons
                                        name="chevron-right"
                                        size={24}
                                        color="#E80C0C"
                                    />
                                </View>

                                <Text>{`- ${demandeAbsence.duree?.texte}`}</Text>
                            </View>
                            <View style={styles.soldeElement}>
                                <Text style={styles.sectionTitle}>Après</Text>
                                <Text>{dureeData.apres} {dureeData.unite}</Text>
                            </View>
                        </View>
                    </ValideurDetailRow>
                )}
                {demandeAbsence.observations && (<ValideurDetailRow title="Commentaire agent">{demandeAbsence.observations}</ValideurDetailRow>)}
                {(status === 'accept' || status === 'reject') && (
                    <ValideurDetailRow>
                        <TextInput
                            style={styles.commentaireInput}
                            multiline
                            numberOfLines={4}
                            placeholder="Ajouter un commentaire"
                            value={commentaire}
                            onChangeText={setCommentaire}
                        />
                    </ValideurDetailRow>
                )}
                <View style={styles.buttonsRow}>
                    {status === 'accept' && (
                        <Pressable onPress={handleValidate} style={styles.buttonPrimary}>
                            <Text style={styles.buttonText}>Confirmer la validation</Text>
                        </Pressable>
                    )}
                    {status === 'reject' && (
                        <Pressable onPress={handleReject} style={styles.buttonSecondary}>
                            <Text style={styles.buttonText}>Confirmer le refus</Text>
                        </Pressable>
                    )}
                </View>
            </View>
        </Dialog>
    )
}
